import { faBuildingColumns, faUser } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import type { RenderField } from '@/components/blocks/collection/types';
import { CollectionCardFieldRenderer } from '@/components/blocks/renderers/collection-card-renderer';
import { Icon } from '@/components/ui/icon';
import { Paragraph } from '@/components/ui/typography';
import type { CollectionCardConfig, CollectionItem } from '@/lib/blocks/collection/blocks';
import { getAlignmentClassName } from '@/lib/blocks/collection/blocks';
import {
  collectionCardMetaTextClass,
  readCollectionFieldNames,
} from '@/lib/blocks/collection/field-utils';
import type {
  CollectionFieldRendererVariant,
  PinnedCaseResultFieldOptions,
} from '@/lib/blocks/collection/index-collection/field-renderer-types';
import { cn } from '@/lib/utils';
import {
  BadgeListField,
  renderRichSummary,
  renderRichTitle,
  renderWidgetSummary,
  renderWidgetTitle,
} from './shared';

export interface BuildCaseResultCollectionRenderFieldOptions {
  item: CollectionItem;
  cardConfig: CollectionCardConfig;
  variant: CollectionFieldRendererVariant;
  pinned?: PinnedCaseResultFieldOptions;
}

export function buildCaseResultCollectionRenderField({
  item,
  cardConfig,
  variant,
  pinned = {},
}: BuildCaseResultCollectionRenderFieldOptions): RenderField {
  const align = cardConfig.align;
  const alignClassName = getAlignmentClassName(align);
  const denseDetailRows = pinned.denseDetailRows === true;

  return (field, value) => {
    switch (field.slot) {
      case 'practiceAreas': {
        const practiceAreas = readCollectionFieldNames(item.practiceAreas);
        if (practiceAreas.length === 0) return null;

        return (
          <BadgeListField
            names={practiceAreas}
            align={align}
            widget={variant === 'widget'}
            size={variant === 'pinned' ? 'md' : 'sm'}
          />
        );
      }

      case 'title': {
        if (variant === 'pinned') {
          return renderRichTitle(item.title, 'text-h2');
        }

        if (variant === 'widget') {
          return renderWidgetTitle(item.title);
        }

        return renderRichTitle(item.title, 'text-xl');
      }

      case 'summary': {
        if (variant === 'widget') {
          return renderWidgetSummary(item.summary, 'text-base leading-normal');
        }

        if (variant === 'pinned') {
          return renderRichSummary(item.summary, 'line-clamp-3 text-base');
        }

        return renderRichSummary(item.summary, 'line-clamp-3 text-sm');
      }

      case 'attorneys':
        return renderCaseResultNamedRow({
          icon: faUser,
          names: readCollectionFieldNames(item.attorneys),
          variant,
          denseDetailRows,
          alignClassName,
        });

      case 'court':
        return renderCaseResultNamedRow({
          icon: faBuildingColumns,
          names: readCollectionFieldNames(item.courts),
          variant,
          denseDetailRows,
          alignClassName,
        });

      case 'button':
        return <CollectionCardFieldRenderer field={field} value={value} align={align} />;

      default:
        return null;
    }
  };
}

function renderCaseResultNamedRow({
  icon,
  names,
  variant,
  denseDetailRows,
  alignClassName,
}: {
  icon: IconDefinition;
  names: string[];
  variant: CollectionFieldRendererVariant;
  denseDetailRows: boolean;
  alignClassName: string;
}) {
  if (names.length === 0) return null;

  const label = names.join(', ');

  if (variant === 'pinned') {
    return (
      <div
        className={cn(
          'flex items-center mt-1',
          denseDetailRows ? 'gap-2.5 text-sm leading-5' : 'gap-3 text-base leading-6',
        )}
      >
        <Icon
          icon={icon}
          className={cn('text-card-foreground/60', denseDetailRows ? 'size-4' : 'size-5')}
        />
        <Paragraph>{label}</Paragraph>
      </div>
    );
  }

  return (
    <div className={cn('flex items-center gap-1.5', alignClassName)}>
      <Icon icon={icon} className="size-4 shrink-0 text-card-foreground/60" />
      <Paragraph className={collectionCardMetaTextClass}>{label}</Paragraph>
    </div>
  );
}
